import { DetectiveIcon } from '@phosphor-icons/react'
import { AlertBanner, Button, TextInput } from '@curri/components'
import { useState, type FormEvent } from 'react'

import { lookupUser } from '../api/admin.ts'
import { useAuth } from '../auth/AuthProvider.tsx'

import { useGhost } from './GhostProvider.tsx'

// The Curri-only control for reading this screen as a partner, and the banner
// that makes it impossible to forget you are doing so.
//
// Renders nothing at all for a partner session — the gate is the caller's own
// `isCurriAdmin`, and the server enforces the same rule on the header regardless
// of what this component decides to draw.

type LookupState =
  | { kind: 'idle' }
  | { kind: 'looking' }
  | { kind: 'failed'; message: string }

/** How the person being viewed is named in the banner. */
const labelFor = (target: {
  emailAddress: string | null
  name: string | null
  userId: number
}): string => target.emailAddress ?? target.name ?? `user ${target.userId}`

const EnterGhost = () => {
  const { apiFetch } = useAuth()
  const { enter } = useGhost()
  const [identifier, setIdentifier] = useState('')
  const [lookup, setLookup] = useState<LookupState>({ kind: 'idle' })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const trimmed = identifier.trim()
    if (!trimmed || lookup.kind === 'looking') return

    setLookup({ kind: 'looking' })
    void (async () => {
      // Resolved here, once, so the header only ever carries a numeric id — an
      // email in a request header ends up in access logs and traces.
      const result = await lookupUser(apiFetch, trimmed)
      if (!result.ok) {
        setLookup({ kind: 'failed', message: result.message })
        return
      }
      setLookup({ kind: 'idle' })
      setIdentifier('')
      enter(result.target)
    })()
  }

  return (
    // Stacked rather than input-beside-button: `TextInput` owns its label and
    // helper/error text, so a side-by-side button can only be aligned to the
    // field itself with a hard-coded offset that breaks the moment either line
    // wraps.
    <form
      onSubmit={submit}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 12,
        border: '1px dashed var(--curri-border)',
        borderRadius: 'var(--curri-radius-md)',
      }}
    >
      <TextInput
        error={lookup.kind === 'failed'}
        errorText={lookup.kind === 'failed' ? lookup.message : undefined}
        helperText="Curri only. Reads the dashboard as that user; nothing can be changed."
        label="View as another user"
        onChange={event => {
          setIdentifier(event.target.value)
          // Clearing the error as they type keeps a stale "no such user" from
          // sitting under a value that has since changed.
          if (lookup.kind === 'failed') setLookup({ kind: 'idle' })
        }}
        placeholder="User id, externalId, or email"
        value={identifier}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          disabled={identifier.trim() === ''}
          label="View as"
          loading={lookup.kind === 'looking'}
          size="base"
          type="submit"
          variant="secondary"
        />
      </div>
    </form>
  )
}

const ActiveGhost = () => {
  const { target, exit } = useGhost()
  if (!target) return null

  return (
    <AlertBanner
      title={`Viewing as ${labelFor(target)} — read-only`}
      type="warning"
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <DetectiveIcon aria-hidden />
        <span style={{ flex: '1 1 240px' }}>
          Everything below is this user&apos;s data, scoped exactly as they see
          it. Curri user {target.userId}
          {target.externalId ? ` · ${target.externalId}` : ''}.
        </span>
        <Button
          label="Stop viewing as"
          onClick={exit}
          size="base"
          variant="secondary"
        />
      </div>
    </AlertBanner>
  )
}

export const GhostBar = () => {
  const { isAvailable, target } = useGhost()

  if (!isAvailable) return null
  return target ? <ActiveGhost /> : <EnterGhost />
}
